// Chat showcases: big catches post themselves to World as a creature card,
// and players can show off a creature they own. Both are chat lines only:
// never a pop-up, never an alert over anyone's screen.
import { ratingOf } from './rating.ts';
import { gradeOf } from './grade.ts';
import type { Rolls } from './rolls.ts';

/**
 * Which big catch this is, or null for an ordinary catch. Only the very rare post on their own:
 * an Aura (1 in 22,000) or Bloodline V (index 4, 1 in 20,000). Bloodline IV (1 in 200) and S+ (~1 in 1,000)
 * were too common for World once many trainers hunt all day; an S+ still posts when it sets a species record.
 * 'grade' stays in the type for record cards and old chat lines.
 */
export type BigCatch = 'aura' | 'bloodline' | 'grade';
export function bigCatch(rolls: Rolls, species: string): BigCatch | null {
  if (rolls.aura) return 'aura';
  if (rolls.bloodline >= 4) return 'bloodline';
  return null;
}

/**
 * Record lines are capped so World stays readable however many trainers hunt: one per trainer an hour,
 * and a few an hour server-wide. A record that misses the cap still goes on the Records board, quietly.
 * Aura and Bloodline V lines are rare enough to always post.
 */
export const AUTO_LINE = {
  /** One per trainer in this long (the log keeps an hour, so keep this at most an hour). */
  trainerGapMs: 60 * 60_000,
  worldPerHour: 6,
} as const;

export interface AutoLineLog { who: number; at: number }

/** Whether trainer `who` may post a record line now. `log` is the recent record lines, any order. */
export function autoLineOk(log: readonly AutoLineLog[], who: number, now: number): boolean {
  const hour = log.filter((x) => now - x.at < 60 * 60_000);
  if (hour.length >= AUTO_LINE.worldPerHour) return false;
  return !log.some((x) => x.who === who && now - x.at < AUTO_LINE.trainerGapMs);
}

/** The log after a record line at `now`, keeping only what still counts. */
export function logAutoLine(log: readonly AutoLineLog[], who: number, now: number): AutoLineLog[] {
  return [...log.filter((x) => now - x.at < 60 * 60_000), { who, at: now }];
}

export const SHOWOFF = {
  /** Between two show-offs by one trainer. */
  gapMs: 30_000,
  /** At most this many in any window. */
  perWindow: 5,
  windowMs: 30 * 60_000,
  /** The same creature again only after this long. */
  sameMs: 10 * 60_000,
} as const;

export interface ShowoffLog { id: number; at: number }

/** How long until this trainer may show creature `id` (0 = now). `log` is their recent show-offs, any order. */
export function showoffWait(log: readonly ShowoffLog[], id: number, now: number): number {
  let wait = 0;
  const recent = log.filter((x) => now - x.at < SHOWOFF.windowMs).sort((a, b) => a.at - b.at);
  const last = recent[recent.length - 1];
  if (last) wait = Math.max(wait, last.at + SHOWOFF.gapMs - now);
  if (recent.length >= SHOWOFF.perWindow) wait = Math.max(wait, recent[recent.length - SHOWOFF.perWindow].at + SHOWOFF.windowMs - now);
  for (const x of recent) if (x.id === id) wait = Math.max(wait, x.at + SHOWOFF.sameMs - now);
  return Math.max(0, wait);
}

/** The log after a show-off at `now`, keeping only what still counts. */
export function logShowoff(log: readonly ShowoffLog[], id: number, now: number): ShowoffLog[] {
  return [...log.filter((x) => now - x.at < SHOWOFF.windowMs), { id, at: now }];
}
